import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';

// Etichette leggibili per i codici "mancanti"
const MANCA_LBL = {
  sito_bloccato: 'sito bloccato',
  partita_iva: 'P.IVA',
  indirizzo: 'indirizzo',
  indirizzo_sporco: 'indirizzo sporco',
};
const fmtData = (at) => (at ? new Date(at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

export default function DominiRivederePage() {
  const [items, setItems] = useState([]);
  const [totale, setTotale] = useState(0);
  const [job, setJob] = useState({ attivo: false, totale: 0, fatti: 0, risolti: 0, corrente: '', fine: null });
  const [loading, setLoading] = useState(true);
  const eraAttivo = useRef(false);

  const carica = useCallback(() => {
    axios.get(`${API}/domini-rivedere`).then(r => {
      setItems(r.data?.items || []);
      setTotale(r.data?.totale || 0);
      if (r.data?.job) setJob(r.data.job);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { carica(); }, [carica]);

  // Poll dello stato job quando è attivo; a fine job ricarica la lista.
  useEffect(() => {
    if (!job.attivo) return undefined;
    eraAttivo.current = true;
    const id = setInterval(() => {
      axios.get(`${API}/domini-rivedere/job`).then(r => setJob(r.data || {})).catch(() => {});
    }, 2000);
    return () => clearInterval(id);
  }, [job.attivo]);
  useEffect(() => {
    if (!job.attivo && eraAttivo.current) {
      eraAttivo.current = false;
      toast.success(`Ri-analisi finita · ${job.risolti || 0} risolti su ${job.totale || 0}`);
      carica();
    }
  }, [job.attivo, job.risolti, job.totale, carica]);

  const rianalizza = async () => {
    try {
      const r = await axios.post(`${API}/domini-rivedere/rianalizza`);
      if (r.data?.avviato) { setJob(j => ({ ...j, attivo: true, totale: r.data.totale, fatti: 0, risolti: 0 })); toast.info(`Ri-analisi avviata su ${r.data.totale} domini`); }
      else toast.info('Nessun dominio da ri-analizzare');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const stop = async () => { try { await axios.post(`${API}/domini-rivedere/job/stop`); toast.info('Interruzione richiesta…'); } catch { toast.error('Errore'); } };
  const elimina = async (dominio) => {
    if (!window.confirm(`Rimuovere ${dominio} dal registro?`)) return;
    try { await axios.delete(`${API}/domini-rivedere/${encodeURIComponent(dominio)}`); carica(); } catch { toast.error('Errore'); }
  };

  const pct = job.totale ? Math.round((job.fatti / job.totale) * 100) : 0;

  return (
    <div style={{ padding: 20, maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Domini da rivedere</h2>
        <span style={{ background: 'var(--surface-2)', color: 'var(--info)', borderRadius: 999, padding: '3px 11px', fontSize: 13, fontWeight: 700 }}>{totale}</span>
        <div style={{ flex: 1 }} />
        {job.attivo ? (
          <button className="btn" onClick={stop} style={{ background: '#fee2e2', color: 'var(--err)', border: '1px solid #fecaca' }}>Ferma</button>
        ) : (
          <button className="btn btn-primary" onClick={rianalizza} disabled={totale === 0}>Ri-analizza tutti</button>
        )}
      </div>
      <p style={{ margin: '0 0 14px', color: 'var(--muted)', fontSize: 13 }}>
        Domini la cui verifica non è risultata completa (sito bloccato/non raggiungibile, oppure manca P.IVA o indirizzo, o indirizzo con caratteri non validi).
        La ri-analisi ri-lancia la verifica del sito su tutti: quelli che si completano vengono tolti automaticamente dall'elenco.
      </p>

      {job.attivo && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
            <span>Ri-analisi in corso… <b>{job.fatti}</b>/{job.totale}{job.corrente ? ` · ${job.corrente}` : ''}</span>
            <span>{pct}%</span>
          </div>
          <div style={{ height: 8, background: 'var(--surface-2)', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg,#6366f1,#4338ca)', transition: 'width .4s' }} />
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--faint)', fontSize: 14, padding: 20 }}>Carico…</div>
      ) : items.length === 0 ? (
        <div style={{ color: 'var(--ok)', fontSize: 14, padding: 20, background: 'var(--surface-2)', border: '1px solid var(--surface-2)', borderRadius: 12 }}>
          Nessun dominio da rivedere. 🎉 Le verifiche dominio recenti sono tutte complete.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12 }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--muted)', borderBottom: '1px solid var(--line)' }}>
                <th style={{ padding: '10px 12px' }}>Dominio</th>
                <th style={{ padding: '10px 12px' }}>Motivo</th>
                <th style={{ padding: '10px 12px' }}>Mancanti</th>
                <th style={{ padding: '10px 12px' }}>Dati trovati</th>
                <th style={{ padding: '10px 12px' }}>Origine</th>
                <th style={{ padding: '10px 12px' }}>Tent.</th>
                <th style={{ padding: '10px 12px' }}>Aggiornato</th>
                <th style={{ padding: '10px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const dati = d.dati || {};
                return (
                  <tr key={d.dominio} style={{ borderTop: '1px solid var(--surface-2)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700 }}>
                      <a href={`https://${d.dominio}`} target="_blank" rel="noreferrer" style={{ color: 'var(--info)', textDecoration: 'none' }}>{d.dominio}</a>
                    </td>
                    <td style={{ padding: '10px 12px', color: d.sito_bloccato ? 'var(--err)' : 'var(--warn)' }}>{d.motivo || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {(d.mancanti || []).map(m => (
                        <span key={m} style={{ display: 'inline-block', background: 'var(--surface-2)', color: 'var(--warn)', borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: 700, marginRight: 4, marginBottom: 2 }}>{MANCA_LBL[m] || m}</span>
                      ))}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--muted)', maxWidth: 320 }}>
                      {dati.ragione_sociale && <div><b>{dati.ragione_sociale}</b></div>}
                      {dati.partita_iva && <div>P.IVA {dati.partita_iva}</div>}
                      {dati.telefono && <div>{dati.telefono}</div>}
                      {dati.indirizzo && <div style={{ color: 'var(--faint)' }}>{dati.indirizzo}</div>}
                      {!dati.ragione_sociale && !dati.partita_iva && !dati.telefono && !dati.indirizzo && <span style={{ color: 'var(--line)' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--muted)' }}>{d.origine || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>{d.tentativi || 1}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmtData(d.updated_at)}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm" onClick={() => elimina(d.dominio)} style={{ color: 'var(--err)' }}>Rimuovi</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
